import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, MapPin, Phone, Mail, Instagram, MessageCircle, Check, Plus, Minus, Settings, Music, Lightbulb, Video, Wrench } from 'lucide-react';

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Background with gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D] via-[#0D0D0D]/90 to-[#0D0D0D]">
        <div className="absolute inset-0 bg-[url('https://canggupodcast.com/assets/studio-interview-new-DW0m3Qpu.jpg')] bg-cover bg-center opacity-30" />
      </div>
      
      {/* Top Label */}
      <div className="absolute top-32 left-1/2 -translate-x-1/2">
        <span className="text-[#F5A623] text-sm tracking-[0.3em] font-medium">Premium Studio</span>
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
        <h1 className="font-heading text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold text-white mb-2 tracking-tight">
          CANGGU PODCAST
        </h1>
        <h2 className="font-heading text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-light text-white/90 mb-8 tracking-wide">
          VIDEO STUDIO
        </h2>
        
        <p className="text-gray-400 text-lg md:text-xl mb-4 max-w-2xl mx-auto">
          A Studio Designed for High Quality Content
        </p>
        <p className="text-gray-500 text-base md:text-lg mb-12 max-w-xl mx-auto">
          Premium equipment. Professional environment. Ready-to-record studios.
        </p>

        <Link
          to="/booking"
          className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg tracking-wide hover:bg-[#FFB84D] transition-all duration-300 transform hover:scale-105"
          data-testid="hero-book-now"
        >
          Book Now
        </Link>
      </div>

      {/* Bottom Location */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 text-gray-400 text-xs sm:text-sm w-max max-w-[90vw] text-center">
        <MapPin className="w-4 h-4 text-[#F5A623] flex-shrink-0" />
        <span>Jl. Pantai Berawa No 150, Canggu, Bali 80361</span>
      </div>
    </section>
  );
};

// Services/Features Section
const ServicesSection = () => {
  const features = [
    { icon: Settings, title: 'Setup Included', desc: 'Ready to record setup' },
    { icon: Music, title: 'Sound', desc: 'Broadcast quality audio' },
    { icon: Lightbulb, title: 'Light', desc: 'Cinematic studio lighting' },
    { icon: Video, title: 'Camera', desc: 'Professional video capture' },
    { icon: Wrench, title: 'Technical Assistance', desc: 'On-site technical support' },
  ];

  return (
    <section id="services" className="py-24 bg-[#0D0D0D]" data-testid="services-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h3 className="text-[#F5A623] text-sm tracking-[0.2em] mb-4 font-medium">Creator Studio Assisted</h3>
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Everything you need for professional content creation
          </h2>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                className="bg-[#121212] border border-[#262626] p-6 text-center hover:border-[#F5A623]/50 transition-all duration-300"
              >
                <div className="flex justify-center mb-4">
                  <IconComponent className="w-8 h-8 text-[#F5A623]" />
                </div>
                <h4 className="text-white font-semibold mb-2 text-sm">{feature.title}</h4>
                <p className="text-gray-500 text-xs">{feature.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Quote Section */}
        <div className="mt-20 text-center">
          <div className="inline-flex items-center gap-3 mb-8">
            <span className="text-white font-heading text-xl tracking-wider">CANGGU</span>
            <span className="text-gray-500 text-sm">Podcast • Video Studio</span>
          </div>
          <blockquote className="max-w-3xl mx-auto">
            <p className="text-gray-400 text-lg md:text-xl italic leading-relaxed">
              "A professional creator studio for entrepreneurs, podcasters, and global content creators where ideas are recorded with clarity and confidence."
            </p>
          </blockquote>
        </div>
      </div>
    </section>
  );
};

// Rooms Section
const RoomsSection = () => {
  const rooms = [
    {
      image: 'https://canggupodcast.com/assets/studio-interview-new-DW0m3Qpu.jpg',
      title: 'Interview Room',
      description: 'Perfect for conversations, debates, and multi-guest podcasts',
      capacity: '2 Guests'
    },
    {
      image: 'https://canggupodcast.com/assets/studio-solo-new-srJ-Dn_K.jpg',
      title: 'Solo Creator Room',
      description: 'Ideal for YouTubers, course creators, and solo podcasters',
      capacity: '1 Person'
    }
  ];

  return (
    <section id="rooms" className="py-24 bg-[#0A0A0A]" data-testid="rooms-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-4">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">Rooms</h2>
          <p className="text-gray-400 text-base max-w-2xl mx-auto">
            Each room includes 2 different sets / ambiances designed for both interviews and solo content creation
          </p>
        </div>

        {/* Rooms Grid */}
        <div className="grid md:grid-cols-2 gap-8 mt-12">
          {rooms.map((room, index) => (
            <div 
              key={index}
              className="group relative overflow-hidden bg-[#121212] border border-[#262626] hover:border-[#F5A623]/50 transition-all duration-500"
            >
              {/* Image */}
              <div className="relative h-80 overflow-hidden">
                <img 
                  src={room.image} 
                  alt={room.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent" />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <h3 className="font-heading text-2xl text-white mb-2">{room.title}</h3>
                <p className="text-gray-400 text-sm mb-4">{room.description}</p>
                <span className="inline-block px-4 py-1 bg-[#F5A623]/10 text-[#F5A623] text-sm border border-[#F5A623]/30">
                  {room.capacity}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Equipment Section
const EquipmentSection = () => {
  const brands = ['SONY', 'SIGMA', 'SHURE', 'RØDE', 'GODOX'];

  return (
    <section className="py-20 bg-[#0D0D0D] border-y border-[#262626]" data-testid="equipment-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">Professional Equipment</h2>
          <p className="text-gray-400">Industry-standard gear for broadcast-quality content</p>
        </div>
        
        {/* Brand Logos */}
        <div className="flex flex-wrap justify-center items-center gap-6 sm:gap-8 md:gap-16">
          {brands.map((brand, index) => (
            <span 
              key={index}
              className="text-gray-500 text-xl sm:text-2xl md:text-3xl font-heading tracking-wider hover:text-[#F5A623] transition-colors duration-300"
            >
              {brand}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const packages = [
    { hours: '2 Hours', price: '2.4M', label: null },
    { hours: '3 Hours', price: '3.2M', label: 'Most Popular' },
    { hours: '5 Hours', price: '5M', label: null },
  ];

  const included = [
    'Professional microphones',
    'Cinematic lighting setup',
    '4K camera recording',
    'Technical assistance',
    '2 room ambiances',
    'Raw footage delivery',
  ];

  return (
    <section id="pricing" className="py-24 bg-[#0A0A0A]" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-4xl md:text-5xl text-white mb-4">Pricing</h2>
          <p className="text-gray-400">Simple, transparent pricing for creators</p>
        </div>

        {/* Starting Price */}
        <div className="text-center mb-12">
          <span className="text-gray-500 text-sm">Starting At</span>
          <div className="flex items-baseline justify-center gap-1 mt-2">
            <span className="text-[#F5A623] text-5xl sm:text-6xl md:text-7xl font-heading font-bold">1.2M</span>
            <span className="text-gray-400 text-lg">IDR/ Hour</span>
          </div>
          <span className="text-gray-500 text-sm">Minimum 2 Hours</span>
        </div>

        {/* Package Options */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {packages.map((pkg, index) => (
            <div 
              key={index}
              className={`relative p-8 text-center border transition-all duration-300 ${
                pkg.label 
                  ? 'bg-[#F5A623]/5 border-[#F5A623]' 
                  : 'bg-[#121212] border-[#262626] hover:border-[#F5A623]/50'
              }`}
            >
              {pkg.label && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#F5A623] text-black text-xs font-semibold">
                  {pkg.label}
                </span>
              )}
              <h3 className="text-white text-xl font-semibold mb-4">{pkg.hours}</h3>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-[#F5A623] text-4xl font-heading font-bold">{pkg.price}</span>
                <span className="text-gray-400">IDR</span>
              </div>
            </div>
          ))}
        </div>

        {/* What's Included */}
        <div className="bg-[#121212] border border-[#262626] p-8 md:p-12">
          <h3 className="font-heading text-2xl text-white mb-8 text-center">What's Included</h3>
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {included.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
                <span className="text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <Link
            to="/booking"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg tracking-wide hover:bg-[#FFB84D] transition-all duration-300"
            data-testid="pricing-book-session"
          >
            Book Your Session
          </Link>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = [
    {
      question: 'How do I book a studio session?',
      answer: 'You can easily book a session by clicking the "Book Now" button on our website. Select your preferred date, time, and duration. You\'ll receive a confirmation email with all the details.'
    },
    {
      question: 'What equipment is included in the rental?',
      answer: 'All sessions include professional microphones (Shure SM7B, Rode PodMic), 4K cameras (Sony), professional lighting (Godox), and access to our RodeCaster Pro mixing console.'
    },
    {
      question: 'Can I bring my own equipment?',
      answer: 'Yes, you\'re welcome to bring your own equipment. Our technical team can help you integrate it with our studio setup for optimal results.'
    },
    {
      question: 'How long is a typical session?',
      answer: 'Sessions are available from 2 to 5 hours. Most podcast recordings take 2-3 hours, while video productions may require more time.'
    },
    {
      question: 'Do you offer editing and post-production services?',
      answer: 'Yes, we offer professional editing and post-production services as an add-on. This includes audio mixing, video editing, color grading, and more.'
    },
  ];

  return (
    <section className="py-24 bg-[#0D0D0D]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4">Frequently Asked Questions</h2>
          <p className="text-gray-400">Everything you need to know before booking your session</p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="border border-[#262626] bg-[#121212] overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#1a1a1a] transition-colors duration-200"
                data-testid={`faq-item-${index}`}
              >
                <span className="text-white font-medium pr-4">{faq.question}</span>
                {openIndex === index ? (
                  <Minus className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
                ) : (
                  <Plus className="w-5 h-5 text-[#F5A623] flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-400 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const images = [
    { src: 'https://canggupodcast.com/assets/gallery-10-CvaxZDES.jpg', alt: 'Solo creator desk setup' },
    { src: 'https://canggupodcast.com/assets/gallery-4-BDEwi8ko.jpg', alt: 'Interview room angle view' },
    { src: 'https://canggupodcast.com/assets/gallery-5-BdP8KqH2.jpg', alt: 'Full studio setup with lighting' },
    { src: 'https://canggupodcast.com/assets/gallery-2-yLnlJKeM.jpg', alt: 'RodeCaster Pro mixing console' },
    { src: 'https://canggupodcast.com/assets/gallery-7-CXtGaZ74.jpg', alt: 'Interview room with armchairs' },
    { src: 'https://canggupodcast.com/assets/gallery-8-CE0JDaFR.jpg', alt: 'Podcast table setup with microphones' },
    { src: 'https://canggupodcast.com/assets/gallery-6-BFijEqIZ.jpg', alt: 'Interview room with professional lighting' },
    { src: 'https://canggupodcast.com/assets/gallery-1-Z2bGhy9G.jpg', alt: 'Studio setup with desk and microphone' },
    { src: 'https://canggupodcast.com/assets/gallery-3-DTShKFpe.jpg', alt: 'Interview room with armchairs and cameras' },
    { src: 'https://canggupodcast.com/assets/gallery-9-qeUPtsrg.jpg', alt: 'Solo creator room with armchair' },
  ];

  const visibleImages = 5;
  const maxSlide = images.length - visibleImages;

  const nextSlide = () => setCurrentSlide(prev => Math.min(prev + 1, maxSlide));
  const prevSlide = () => setCurrentSlide(prev => Math.max(prev - 1, 0));

  return (
    <section className="py-24 bg-[#0A0A0A] overflow-hidden" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4">Gallery</h2>
          <p className="text-gray-400">Discover our professional podcast and video studio spaces</p>
        </div>

        {/* Gallery Slider */}
        <div className="relative px-6 sm:px-0">
          {/* Images Container */}
          <div className="overflow-hidden">
            <div 
              className="flex gap-4 transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentSlide * (100 / visibleImages + 1)}%)` }}
            >
              {images.map((image, index) => (
                <div 
                  key={index}
                  className="flex-shrink-0 w-[70vw] sm:w-[45vw] md:w-[calc(20%-12px)] aspect-[4/3] overflow-hidden"
                >
                  <img 
                    src={image.src}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="absolute left-0 sm:-left-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D0D0D] border border-[#262626] flex items-center justify-center hover:border-[#F5A623] transition-colors disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="gallery-prev"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
          <button
            onClick={nextSlide}
            disabled={currentSlide >= maxSlide}
            className="absolute right-0 sm:-right-2 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-[#0D0D0D] border border-[#262626] flex items-center justify-center hover:border-[#F5A623] transition-colors disabled:opacity-30 disabled:cursor-not-allowed z-10"
            data-testid="gallery-next"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-24 bg-[#0D0D0D]" data-testid="contact-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-white mb-4">Location & Contact</h2>
          <p className="text-gray-400">Visit us in the heart of Canggu, Bali</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Map */}
          <div className="bg-[#121212] border border-[#262626] h-80 md:h-full min-h-[300px] overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3944.5257!2d115.1494!3d-8.6578!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zOMKwMzknMjguMSJTIDExNcKwMDgnNTcuOCJF!5e0!3m2!1sen!2sid!4v1699000000000!5m2!1sen!2sid"
              width="100%"
              height="100%"
              style={{ border: 0, filter: 'grayscale(100%) invert(92%) contrast(83%)' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Studio Location"
            />
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            {/* Address */}
            <div>
              <h3 className="text-[#F5A623] text-sm tracking-wider mb-3 font-medium">Address</h3>
              <p className="text-white text-lg">Jl. Pantai Berawa No 150</p>
              <p className="text-gray-400">Canggu, Bali 80361</p>
              <p className="text-gray-400">Indonesia</p>
            </div>

            {/* Contact Links */}
            <div className="space-y-4">
              <a 
                href="https://wa.me/628133975057"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-300 hover:text-[#F5A623] transition-colors group"
              >
                <MessageCircle className="w-5 h-5 text-[#F5A623]" />
                <span>WhatsApp</span>
              </a>
              <a 
                href="tel:+628133975057"
                className="flex items-center gap-3 text-gray-300 hover:text-[#F5A623] transition-colors"
              >
                <Phone className="w-5 h-5 text-[#F5A623]" />
                <span>+62 813-3975-057</span>
              </a>
              <a 
                href="mailto:contact@canggupodcast.com"
                className="flex items-center gap-3 text-gray-300 hover:text-[#F5A623] transition-colors"
              >
                <Mail className="w-5 h-5 text-[#F5A623]" />
                <span>contact@canggupodcast.com</span>
              </a>
              <a 
                href="https://instagram.com/canggupodcast"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-300 hover:text-[#F5A623] transition-colors"
              >
                <Instagram className="w-5 h-5 text-[#F5A623]" />
                <span>@canggupodcast</span>
              </a>
            </div>

            {/* CTA Button */}
            <Link
              to="/booking"
              className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg tracking-wide hover:bg-[#FFB84D] transition-all duration-300 mt-4"
              data-testid="contact-book-now"
            >
              Book Now
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

import { Footer as SharedFooter } from '../components/Footer';

// Footer
const Footer = SharedFooter;

// Main HomePage Component
export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] overflow-x-hidden" data-testid="home-page">
      <HeroSection />
      <ServicesSection />
      <RoomsSection />
      <EquipmentSection />
      <PricingSection />
      <FAQSection />
      <GallerySection />
      <ContactSection />
      <Footer />
    </div>
  );
}
